import { useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { track } from '../../../tracking/track';
import { events } from '../../../tracking/events';
import { getCreateInvoicesURL, getCommerceHomeProcessorPickerIframeOpenURL, getPaymentLinksURL, getBillingWizardURL } from 'growth-payments-core/urls/urls';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { View } from 'foundations-components/components/View';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { Button } from 'foundations-components/transitional/components/Button';
import ModalWrapper from '../ModalWrapper';
import { NextIcon } from 'foundations-assets/icons/components/NextIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const TOOL_CONFIG = {
  payments: {
    modalName: 'createPayment',
    illustrationName: 'payments-button',
    url: `${getPaymentLinksURL()}/create`,
    ctaMessage: 'embedPoints.ctas.createAPaymentLink',
    numBulletPoints: 2
  },
  subscriptions: {
    modalName: 'createSubscription',
    illustrationName: 'payments-subscriptions',
    url: getBillingWizardURL(),
    ctaMessage: 'embedPoints.ctas.createASubscription',
    numBulletPoints: 3
  },
  invoices: {
    modalName: 'createInvoice',
    illustrationName: 'invoices',
    url: getCreateInvoicesURL(),
    ctaMessage: 'embedPoints.ctas.createAnInvoice',
    numBulletPoints: 3
  },
  setUpPayments: {
    modalName: 'setUpPayments',
    illustrationName: 'payments',
    url: getCommerceHomeProcessorPickerIframeOpenURL(),
    ctaMessage: 'embedPoints.ctas.goToPayments',
    numBulletPoints: 3
  }
};

// UI Components
const ListItemWithIcon = ({
  children
}) => /*#__PURE__*/_jsxs(View, {
  marginBlockEnd: "300",
  children: [/*#__PURE__*/_jsx(IconCircle, {
    icon: NextIcon,
    color: "fill-accent-purple-pressed",
    size: "100",
    backgroundColor: "fill-accent-purple-subtle",
    borderColor: "fill-accent-purple-subtle",
    padding: "100",
    marginInlineEnd: "200"
  }), children]
});
const ToolModalPrimaryButton = props => /*#__PURE__*/_jsx(Button, Object.assign({
  use: "primary",
  "data-test-id": "create-tool-button"
}, props));
const CreateToolModal = ({
  onClose,
  page,
  tool
}) => {
  const trackButtonClick = useCallback(trackValue => {
    track(events.paymentsInterstitialModalInteraction, {
      action: 'click',
      screen: page,
      value: `createTool-${trackValue}`
    });
  }, [page]);

  // Get current tool configuration
  const config = TOOL_CONFIG[tool];
  const {
    modalName,
    illustrationName
  } = config;

  // Common button components
  const createToolButtons = {
    payments: /*#__PURE__*/_jsx(ToolModalPrimaryButton, {
      href: TOOL_CONFIG.payments.url,
      onClick: () => trackButtonClick('payments'),
      external: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: TOOL_CONFIG.payments.ctaMessage
      })
    }),
    invoices: /*#__PURE__*/_jsx(ToolModalPrimaryButton, {
      href: TOOL_CONFIG.invoices.url,
      onClick: () => trackButtonClick('invoices'),
      external: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: TOOL_CONFIG.invoices.ctaMessage
      })
    }),
    subscriptions: /*#__PURE__*/_jsx(ToolModalPrimaryButton, {
      href: TOOL_CONFIG.subscriptions.url,
      onClick: () => trackButtonClick('subscriptions'),
      external: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: TOOL_CONFIG.subscriptions.ctaMessage
      })
    }),
    setUpPayments: /*#__PURE__*/_jsx(ToolModalPrimaryButton, {
      href: TOOL_CONFIG.setUpPayments.url,
      onClick: () => trackButtonClick('setUpPayments'),
      external: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: TOOL_CONFIG.setUpPayments.ctaMessage
      })
    })
  };

  // Dropdown component
  const ToolSelectionDropdown = props => /*#__PURE__*/_jsx(Dropdown, Object.assign({
    "data-test-id": "tool-selection-dropdown",
    buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "embedPoints.ctas.collectPaymentsWithTools"
    }),
    placement: "bottom"
  }, props, {
    children: /*#__PURE__*/_jsxs(List, {
      children: [/*#__PURE__*/_jsx(ListItem, {
        children: createToolButtons.payments
      }), /*#__PURE__*/_jsx(ListItem, {
        children: createToolButtons.invoices
      }), /*#__PURE__*/_jsx(ListItem, {
        children: createToolButtons.subscriptions
      })]
    })
  }));

  // Determine primary and secondary CTAs based on tool type
  const primaryCTA = tool === 'payments' ? /*#__PURE__*/_jsx(ToolSelectionDropdown, {
    buttonUse: "primary"
  }) : createToolButtons[tool];
  const secondaryCTA = tool === 'setUpPayments' ? /*#__PURE__*/_jsx(ToolSelectionDropdown, {}) : null;
  const FULL_MODAL_NAME = `createToolModal.${modalName}`;

  // Modal content
  const Body = /*#__PURE__*/_jsx(List, {
    marginBlockStart: "300",
    children: [...Array(config.numBulletPoints)].map((_, num) => /*#__PURE__*/_jsx(ListItemWithIcon, {
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: `embedPoints.${FULL_MODAL_NAME}.p${num + 1}`
      })
    }, num))
  });
  const Footer = /*#__PURE__*/_jsxs(_Fragment, {
    children: [primaryCTA, secondaryCTA]
  });
  return /*#__PURE__*/_jsx(ModalWrapper, {
    onClose: onClose,
    modalName: FULL_MODAL_NAME,
    modalSubTitle: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `embedPoints.${FULL_MODAL_NAME}.subTitle`
    }),
    Body: Body,
    Footer: Footer,
    illustrationName: illustrationName,
    paddingBlockStart: "1200",
    paddingBlockEnd: "500",
    paddingInlineEnd: "1000"
  });
};
export default CreateToolModal;